import type { Role } from '@akpl/shared';

/**
 * Sidebar of Figma CMS-02, in order. `to` is null for a section a later work package still has
 * to build: it stays visible but disabled with a "soon" tag. Phase 1 hides "Products & prices"
 * entirely (docs/06 §2.11) and there is no "Suscripciones" entry — access is granted with a
 * grant on the Acceso screen (docs/07 §1, §3).
 */
export interface NavItem {
  /** Key under `cms.nav.*`. */
  key: string;
  to: string | null;
  /** Minimum role, `viewer` when omitted (docs/06 §1). */
  role?: Role;
}

export interface NavGroup {
  /** Key under `cms.nav.groups.*`, or null for the ungrouped top entry. */
  key: string | null;
  items: NavItem[];
}

export const NAV: NavGroup[] = [
  { key: null, items: [{ key: 'dashboard', to: '/' }] },
  {
    key: 'catalog',
    items: [
      { key: 'songs', to: '/songs' },
      { key: 'artists', to: '/artists' },
      { key: 'playlists', to: '/playlists' },
      { key: 'heroes', to: '/heroes' },
      { key: 'home', to: '/home' },
      // `/review` (Catalog review) is hidden from the sidebar on request; the route itself still exists.
    ],
  },
  {
    key: 'access',
    items: [
      { key: 'grants', to: '/access' },
      { key: 'users', to: '/users', role: 'admin' },
      { key: 'devices', to: '/devices' },
    ],
  },
  {
    key: 'operations',
    items: [
      { key: 'sessions', to: '/sessions' },
      { key: 'audit', to: '/audit' },
      { key: 'settings', to: '/settings', role: 'admin' },
    ],
  },
];

const RANK: Record<Role, number> = { member: 0, viewer: 1, editor: 2, admin: 3 };

export function visibleNav(role: Role | null): NavGroup[] {
  if (!role) return [];
  return NAV.map((group) => ({
    ...group,
    items: group.items.filter((item) => RANK[role] >= RANK[item.role ?? 'viewer']),
  })).filter((group) => group.items.length > 0);
}
